'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { cn } from 'cn';
import { AlertCircle } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { ImportQueueController } from '../types/import-queue-controller';
interface ImportQueueListControlsProps {
  controller: ImportQueueController;
}
export function ImportQueueListControls(t0) {
  const $ = _c(23);
  const {
    controller
  } = t0;
  const preferences = controller.listPreferences;
  const actions = controller.listActions;
  const [confirmOpen, setConfirmOpen] = useState(false);
  if (!preferences && !actions) {
    return null;
  }
  const failedOnly = preferences?.failedOnly ?? false;
  const showFailedFilter = Boolean(preferences?.onFailedOnlyChange) && ((preferences?.failedCount ?? 0) > 0 || failedOnly);
  let t1;
  if ($[0] !== actions) {
    t1 = () => {
      actions?.onClearAll();
      setConfirmOpen(false);
    };
    $[0] = actions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const runClearAll = t1;
  let t2;
  if ($[2] !== actions?.clearAllRequiresConfirm || $[3] !== runClearAll) {
    t2 = () => {
      if (actions?.clearAllRequiresConfirm) {
        setConfirmOpen(true);
        return;
      }
      runClearAll();
    };
    $[2] = actions?.clearAllRequiresConfirm;
    $[3] = runClearAll;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onClearClick = t2;
  let t3;
  if ($[5] !== controller.domainKey || $[6] !== failedOnly || $[7] !== preferences || $[8] !== showFailedFilter) {
    t3 = preferences ? <div className="flex flex-wrap items-center gap-2"><Switch id={`import-queue-hide-completed-${controller.domainKey}`} checked={preferences.hideCompleted} onCheckedChange={preferences.onHideCompletedChange} aria-label="Hide completed imports" /><Label htmlFor={`import-queue-hide-completed-${controller.domainKey}`} className="text-muted-foreground cursor-pointer text-xs font-normal">Hide completed</Label>{showFailedFilter ? <Button type="button" variant="ghost" size="sm" aria-pressed={failedOnly} className={cn("h-7 gap-1 px-2 text-xs", {
        "bg-destructive/10 text-destructive hover:bg-destructive/15 hover:text-destructive": failedOnly,
        "text-muted-foreground hover:text-foreground": !failedOnly
      })} onClick={() => preferences.onFailedOnlyChange?.(!failedOnly)}><AlertCircle className="size-3.5" aria-hidden={true} />{`Errors only (${(preferences.failedCount ?? 0).toLocaleString()})`}</Button> : null}</div> : <span />;
    $[5] = controller.domainKey;
    $[6] = failedOnly;
    $[7] = preferences;
    $[8] = showFailedFilter;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== actions || $[11] !== onClearClick) {
    t4 = actions ? <Button type="button" variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground h-7 px-2 text-xs" disabled={actions.clearAllDisabled} onClick={onClearClick}>Clear all</Button> : null;
    $[10] = actions;
    $[11] = onClearClick;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <div className="flex flex-wrap items-center justify-between gap-2">{t3}{t4}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== actions || $[17] !== confirmOpen || $[18] !== runClearAll) {
    t6 = actions?.clearAllRequiresConfirm ? <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{actions.clearAllConfirmTitle ?? "Clear import list?"}</AlertDialogTitle><AlertDialogDescription>{actions.clearAllConfirmDescription ?? "This removes every item from the imports list. Queued uploads are canceled; a file already uploading may still finish."}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={runClearAll}>Clear all</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog> : null;
    $[16] = actions;
    $[17] = confirmOpen;
    $[18] = runClearAll;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}